import { html, LitElement, nothing, TemplateResult } from 'lit';
import { GzRuleCriteriaWebComponent } from '../index';
import { GzSingleSelectWebComponent } from '../../../../../ui/gzSingleSelectDropdown';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzDialog, GzDialogEvents } from '../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../ui/gzDialog';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ConnectionOptions, ConnectionOptionsProtocol, CustomRulesEvents, DetectionRuleType, FilterFields, GridType, Operators, Type } from '../../../../customRulesGrid/types';
import { createDialog, validateAlert, validateRule } from '../../../utils';
import { CHANGE_TO_ADVANCED } from '../../../constants';
import { Feature } from '@nirvana/shared/constants';
import { htmlEncode } from '../../../../../utils/escape.utils';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { checkRuleCriteriaTypes } from '../../../../customRulesGrid/utils';
import { GzToastNotificationWebComponent } from '../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../ui/gzToastNotification/type';

@customElement('gz-rule-definition')
export class GzRuleDefinitionWebComponent extends LitElement {
  @property({
    type: String,
    attribute: false,
    reflect: false,
  })
  gridType;

  @property({ type: Boolean })
  isGZ = false;

  @query('#add-new-button')
  private readonly addNewButton: GzButtonWebComponent;

  @query('#detection')
  private readonly detection: GzSingleSelectWebComponent;

  @property({
    type: Object,
    attribute: false,
    reflect: false,
  })
  rule: any = null;

  @state()
  isDialogDisplayed = false;

  private valid = true;

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="w-full">
        <div class="d-flex">${this.renderDetection()}</div>
        <div class="d-flex flex-column">
          ${this.renderAllRuleCriteria()}
          ${this.renderInfoBox()}
        </div>
      </div>
    `;
  }

  private renderDetection(): TemplateResult {
    let label;
    const detectionOptions = [
      {
        title: translateFn('Process'),
        value: DetectionRuleType.PROCESS,
      },
      {
        title: translateFn('File'),
        value: DetectionRuleType.FILE,
      },
      {
        title: translateFn('Connection'),
        value: DetectionRuleType.CONNECTION,
      },
      {
        title: translateFn('Registry'),
        value: DetectionRuleType.REGISTRY,
      }
    ];
    if (this.gridType === GridType.DETECTION) {
      label = translateFn('Consider as detection every') + ':';
    } else {
      detectionOptions.push({
        title: translateFn('User connection'),
        value: DetectionRuleType.USER_LOGIN,
      }, {
        title: translateFn('Email'),
        value: DetectionRuleType.EMAIL,
      });
      if (!this.isGZ) {
        detectionOptions.push({
          title: translateFn('Application'),
          value: DetectionRuleType.APPLICATION,
        }, {
          title: translateFn('Key vault'),
          value: DetectionRuleType.KEY_VAULT,
        }, {
          title: translateFn('Role'),
          value: DetectionRuleType.ROLE,
        }, {
          title: translateFn('Policy'),
          value: DetectionRuleType.POLICY,
        }, {
          title: translateFn('Sharing link'),
          value: DetectionRuleType.SHARING_LINK,
        }, {
          title: translateFn('Flow'),
          value: DetectionRuleType.FLOW,
        }, {
          title: translateFn('URL'),
          value: DetectionRuleType.URL,
        }, {
          title: translateFn('SSH key'),
          value: DetectionRuleType.SSH_KEY,
        }, {
          title: translateFn('Launch template'),
          value: DetectionRuleType.LAUNCH_TEMPLATE,
        }, {
          title: translateFn('Service principal'),
          value: DetectionRuleType.SERVICE_PRINCIPAL,
        }, {
          title: translateFn('User group'),
          value: DetectionRuleType.USER_GROUP,
        }, {
          title: translateFn('Automation account'),
          value: DetectionRuleType.AUTOMATION_ACCOUNT,
        }, {
          title: translateFn('Automation account hook'),
          value: DetectionRuleType.AUTOMATION_ACCOUNT_HOOK,
        }, {
          title: translateFn('API'),
          value: DetectionRuleType.API,
        }, {
          title: translateFn('Certificate authority'),
          value: DetectionRuleType.CERTIFICATE_AUTHORITY,
        }, {
          title: translateFn('Bucket'),
          value: DetectionRuleType.BUCKET,
        }, {
          title: translateFn('Bitbucket repository'),
          value: DetectionRuleType.BITBUCKET_REPOSITORY,
        }, {
          title: translateFn('Jira project'),
          value: DetectionRuleType.JIRA_PROJECT,
        }, {
          title: translateFn('Confluence page'),
          value: DetectionRuleType.CONFLUENCE_PAGE,
        });
      }
      label = translateFn('Consider as exclusion every') + ':';
    }

    // had to do this because setting .value on dropdown does not show correct value when opening dialog and press close
    setTimeout(() => this.detection.setFormValue(this.rule.query.type), 0);

    return html`
      <div class="d-flex flex-row align-items-center w-full section-detection">
        <gz-tooltip class="label" tooltipText="${label}" isEllipsis>
          <gz-text fontSize="13">${label}</gz-text>
        </gz-tooltip>
        <gz-dropdown
          class="content"
          .options="${detectionOptions}"
          @selectionChanged="${this.handleDetectionSelectionChange}"
          id="detection"
        ></gz-dropdown>
        ${this.gridType === GridType.DETECTION ? this.renderAdvancedButton() : nothing}
      </div>
    `;
  }

  protected renderAdvancedButton() {
    if (this.rule._id || !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_YARA_DETECTION_RULES])) {
      // we do not show button if we edit the rule
      return nothing;
    }

    return html`
      <gz-button
        class="mr-3 ml-2"
        type="quiet"
        color="primary"
        buttonheight="small"
        buttonfontsize="small"
        lowercase=""
        theme="light"
        @click="${this.handleAdvancedButtonClick}"
      >
        ${translateFn('YARA')}
      </gz-button>`;
  }

  private renderAllRuleCriteria(): TemplateResult {
    return html`
      <div class="d-flex flex-column mt-2 mb-2 section-matching w-full">
        <div class="d-flex flex-row align-items-start">
          <gz-tooltip class="label" tooltipText="${translateFn('Matching the following') + ':'}" isEllipsis>
            <gz-text class="label" fontSize="13">${translateFn('Matching the following') + ':'}</gz-text>
          </gz-tooltip>
          <div class="d-flex flex-column mb-2 inputs-section content">${this.renderRuleCriteria()}</div>
        </div>
        <gz-button id="add-new-button" class="add-new-btn" type="outline" @click="${() => this.addNewRule()}"
        ><span class="icon ds-icon-add"></span><span class="button-text">${translateFn('Add new')}</span></gz-button
        >
      </div>
    `;
  }

  private renderRuleCriteria() {
    if (this.rule.query.detection) {
      let alertRuleCriteria = this.rule.query.rules.find(ruleCriteria => ruleCriteria.criteriaValue === FilterFields.ALERT_NAME);
      if (!alertRuleCriteria) {
        alertRuleCriteria = {
          criteriaValue: FilterFields.ALERT_NAME,
          inputValue: htmlEncode(this.rule.query.detection),
          operatorValue: Operators.IS,
        };
        this.rule.query.rules.push(alertRuleCriteria);
      }
    }
    const rules = this.rule.query.rules.map(ruleCriteria => {
      const ruleComponent = this.renderSingleCriteria(ruleCriteria);
      this.addRuleCriteriaListeners(ruleComponent);
      return ruleComponent;
    });
    return html`${rules}`;
  }

  private handleAlertDelete() {
    if (this.rule.query.detection) {
      delete this.rule.query.detection;
    }
  }

  private addRuleCriteriaListeners(ruleCriteriaComponent: GzRuleCriteriaWebComponent): void {
    ruleCriteriaComponent.addEventListener(CustomRulesEvents.DELETE, () => {
      this.handleDelete(this.rule.query.rules.indexOf(ruleCriteriaComponent.ruleCriteria));
    });

    ruleCriteriaComponent.addEventListener(CustomRulesEvents.EDIT_CRITERIA, () => {

      const allCriteriaComponents = this.shadowRoot?.querySelectorAll('gz-rule-criteria') as NodeListOf<GzRuleCriteriaWebComponent>;
      const disabledOptions = this.checkForDisabledOptions();

      const detection = Array.from(allCriteriaComponents).find((element) => element.ruleCriteria.criteriaValue === FilterFields.ALERT_NAME);
      if (this.rule.query.detection && !detection) {
        delete this.rule.query.detection;
      }

      if (detection && Array.isArray(detection.ruleCriteria.inputValue)) {
        detection.ruleCriteria.inputValue = detection.ruleCriteria.inputValue[0];
      }

      allCriteriaComponents.forEach(rule => {
        if (rule.disabledOptions !== disabledOptions) {
          rule.disabledOptions = disabledOptions;
        }
      });
    });
  }

  private renderSingleCriteria(ruleCriteria) {
    const ruleCriteriaComponent = new GzRuleCriteriaWebComponent();
    ruleCriteriaComponent.hasDeleteButton = this.rule.query.rules.length > 1;
    ruleCriteriaComponent.categoryValue = this.rule.query.type;
    ruleCriteriaComponent.ruleCriteria = ruleCriteria;
    ruleCriteriaComponent.gridType = this.gridType;
    ruleCriteriaComponent.isGZ = this.isGZ;
    ruleCriteriaComponent.disabledOptions = this.checkForDisabledOptions();
    return ruleCriteriaComponent;
  }

  private checkForDisabledOptions() {
    const options = {};
    const category = this.rule.query.type;

    if (category === DetectionRuleType.CONNECTION) {
      let protocol = null;
      this.rule.query.rules.forEach(rule => {
        if (ConnectionOptionsProtocol[rule.criteriaValue]) {
          protocol = ConnectionOptionsProtocol[rule.criteriaValue];
        }
      });
      if (protocol) {
        const connectionOptions = Object.values(ConnectionOptions);
        options[DetectionRuleType.CONNECTION] = connectionOptions.filter(option => {
          return ConnectionOptionsProtocol[option] !== protocol && ConnectionOptionsProtocol[option] !== undefined;
        });
      }
    }

    const filtersList = this.getRuleFilters();
    if (filtersList && filtersList.length && filtersList.length === 1) {
      options[category] = FilterFields.ALERT_NAME;
    }


    return options;
  }

  public checkRuleCriteriaChanged() {
    for (const rule of this.rule.query.rules) {
      if (rule.criteriaValue != null || rule.operatorValue != null || rule.inputValue != null) {
        return true;
      }
    }
    return false;
  }

  private handleDetectionSelectionChange(event: CustomEvent) {
    const selectedDetection = event.detail;

    if (this.isDialogDisplayed || this.rule.query.type === selectedDetection.value) {
      return;
    }

    if (!this.checkRuleCriteriaChanged() && this.rule.query.rules.length === 1) {
      this.rule.query.type = selectedDetection.value;
      this.rule.query.rules = this.rule.query.rules.map(ruleCriteria => {
        ruleCriteria.categoryValue = selectedDetection.value;
        return ruleCriteria;
      });
      this.requestUpdate('rule');
      return;
    }

    const dialogTitle = translateFn('Discard existing rule?');
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>${translateFn('Changing the category type will remove all existing criteria.')}</p>`;

    const dialogFooter = document.createElement('div');
    const discardButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();
    discardButton.innerText = translateFn('Discard');
    discardButton.setAttribute('type', 'outline');
    discardButton.setAttribute('color', 'negative');
    discardButton.id = 'discard-button';
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    cancelButton.id = 'cancel-button';
    dialogFooter.appendChild(discardButton);
    dialogFooter.appendChild(cancelButton);

    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);
    this.isDialogDisplayed = true;

    discardButton.addEventListener('click', () => {
      this.rule.query.rules = [];
      this.rule.query.detection = '';
      this.addNewRule();
      dialog.dismiss();
      this.isDialogDisplayed = false;
      this.rule.query.type = selectedDetection.value;
      this.validateRuleType();
    });
    cancelButton.addEventListener('click', () => {
      dialog.dismiss();
      this.isDialogDisplayed = false;
    });
    dialog.addEventListener(GzDialogEvents.CANCEL, () => {
      this.isDialogDisplayed = false;
      GzToastNotificationWebComponent.closeAllToasts();
    });
  }

  private handleAdvancedButtonClick() {
    if (this.checkRuleCriteriaChanged()) {
      createDialog({
        headline: translateFn('Switch to YARA rule definition?'),
        textContent: translateFn('You will lose all current rule criteria.'),
        confirmButtonText: translateFn('Switch'),
        cancelButtonText: translateFn('Cancel'),
        confirmCallback: () => {
          this.dispatchEvent(
            new CustomEvent(CHANGE_TO_ADVANCED, {
              bubbles: true,
              cancelable: true,
              composed: true,
            }),
          );
        },
      });
      return;
    }

    this.dispatchEvent(
      new CustomEvent(CHANGE_TO_ADVANCED, {
        bubbles: true,
        cancelable: true,
        composed: true,
      }),
    );
  }

  private addNewRule() {
    this.rule.query.rules.push({
      criteriaValue: null,
      operatorValue: null,
      inputValue: null,
    });
    this.requestUpdate('rule');
  }

  private handleDelete(index: number) {
    if (this.rule.query.rules[index].criteriaValue === FilterFields.ALERT_NAME) {
      this.handleAlertDelete();
    }
    this.rule.query.rules = this.rule.query.rules.filter((_, i) => i !== index);
    this.requestUpdate('rule');
  }

  private markComponentFieldsWithError(criteriaList) {
    if (criteriaList) {
      criteriaList.forEach(criteria => {
        criteria.markFieldsWithError();
      });
    }
  }

  /**
   * Returns a list of rule criteria. Filters such as alert name are not taken into consideration as actual rule criteria
   * Criteria for EDRQL have an AND relationship between them
   * @return {any}
   * @private
   */
  private getRuleCriteria(): any {
    return this.rule.query.rules.filter(ruleCriteria => {
      return ruleCriteria.criteriaValue !== FilterFields.ALERT_NAME;
    });
  }

  /**
   * Returns a list of rule filters.
   * Filters for EDRQL have an OR relationship between them
   * @return {any}
   * @private
   */
  private getRuleFilters(): any {
    return this.rule.query.rules.filter(ruleCriteria => {
      return ruleCriteria.criteriaValue === FilterFields.ALERT_NAME;
    });
  }

  private validateRuleCriteria(ruleCriteria) {
    if (!ruleCriteria || !ruleCriteria.length) {
      this.valid = false;
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Cannot add an exclusion based only on the alert name.'),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
    for (const criteria of ruleCriteria) {
      if (!validateRule(criteria, this.rule.query.type)) {
        this.valid = false;
      }
    }
  }

  private validateRuleFilters(filtersList) {
    if (filtersList && filtersList.length && filtersList.length > 1) {
      this.valid = false;
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Cannot add more than one exclusion based on alert name.'),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
    if (
      filtersList &&
      filtersList.length &&
      filtersList.length === 1 &&
      filtersList[0] &&
      !validateAlert(filtersList[0])
    ) {
      this.valid = false;
    }
  }

  private validateRuleType() {
    const ruleType = checkRuleCriteriaTypes(this.rule, this.gridType);
    if (ruleType === Type.EDR_XDR) {
      this.valid = false;
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('You cannot create rules that use both EDR and XDR labeled criteria.'),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      });
    }
  }

  public handleValidation() {
    this.refreshValidStatus();
    GzToastNotificationWebComponent.closeAllToasts();
    const filteredCriteriaList = this.getRuleCriteria();
    this.validateRuleCriteria(filteredCriteriaList);
    const filtersList = this.getRuleFilters();
    this.validateRuleFilters(filtersList);
    this.validateRuleType();
    if (!this.valid) {
      const criteriaList = this.shadowRoot?.querySelectorAll('gz-rule-criteria');
      this.markComponentFieldsWithError(criteriaList);
    } else {
      GzToastNotificationWebComponent.closeAllToasts();
    }
    return this.valid;
  }

  private renderInfoBox() {
    if (this.gridType === GridType.DETECTION || this.isGZ) {
      return;
    }

    return html`
      <gz-info-box class="section-info-box" severity="${InfoBoxSeverity.INFORMATIVE}">
        ${translateFn(
          'Exclusion criteria marked with EDR or XDR labels apply only to their respective technologies. Exclusion criteria without a label apply to both.')}
      </gz-info-box>`;
  }

  private refreshValidStatus() {
    this.valid = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rule-definition': GzRuleDefinitionWebComponent;
  }
}
